// When does mouse movement on the locked screen mean "someone wants back in"?
//
// Not on a nudge: a bumped desk, a cable tug or a hand resting on the mouse must leave
// the screensaver running. A click or a key is the plain way in (screen-lock.tsx); the
// mouse alone counts only when the movement is deliberate:
//
//   - **brisk** — a quick shake or a flick across the screen: travel summed with an
//     exponential decay (time constant BRISK_DECAY_MS) passes BRISK_DISTANCE;
//   - **continuous** — the mouse keeps moving, without pausing longer than
//     STREAK_GAP_MS, for STREAK_MS, covering at least STREAK_DISTANCE at an average of
//     STREAK_MIN_SPEED or more (a mouse sliding on a tilted desk is not a person).
//
// Pure (positions in px, times in ms) so the thresholds are tested without a DOM.

export const BRISK_DISTANCE = 700;
export const BRISK_DECAY_MS = 350;
export const STREAK_MS = 1500;
export const STREAK_GAP_MS = 200;
export const STREAK_DISTANCE = 400;
/** px per ms (= 150 px/s). */
export const STREAK_MIN_SPEED = 0.15;

export interface IWakeGesture {
  /** Feed one pointer position; true once the movement reads as deliberate. */
  move(x: number, y: number, at: number): boolean;
}

export function createWakeGesture(): IWakeGesture {
  let last: { x: number; y: number; at: number } | null = null;
  let energy = 0;
  let streakStart = 0;
  let streakDistance = 0;

  return {
    move(x, y, at) {
      if (!last) {
        last = { x, y, at };
        streakStart = at;
        return false;
      }
      const dt = Math.max(0, at - last.at);
      const step = Math.hypot(x - last.x, y - last.y);
      last = { x, y, at };

      energy = energy * Math.exp(-dt / BRISK_DECAY_MS) + step;

      if (dt > STREAK_GAP_MS) {
        streakStart = at;
        streakDistance = 0;
      } else {
        streakDistance += step;
      }

      const streak = at - streakStart;
      const continuous =
        streak >= STREAK_MS && streakDistance >= STREAK_DISTANCE && streakDistance / streak >= STREAK_MIN_SPEED;
      return energy >= BRISK_DISTANCE || continuous;
    },
  };
}
